import type { UserRole } from '../types'

import { useAuth } from './useAuth'
import { useAdminView } from './useAdminView'

/**
 * Hook for role-based access control with admin view toggle support.
 * When admin view is disabled, admin users will see the app as regular users.
 * @returns Object with role checking utilities
 */
export const useRoles = () => {
  const { user, isUserRole, requireRole } = useAuth()
  const { isAdminViewEnabled } = useAdminView()

  // Core role checks (based on actual user permissions)
  const isActualAdmin = isUserRole('admin')
  const isActualUser = isUserRole('user')

  // Effective role checks (considering admin view toggle)
  // When admin view is disabled, admins are treated as regular users for UI purposes
  const isAdmin = isActualAdmin && isAdminViewEnabled
  const canAccessAdminFeatures = isActualAdmin && isAdminViewEnabled

  return {
    // User state
    user,
    isAuthenticated: !!user,

    // Effective role checking (respects admin view toggle)
    isUser: isActualUser,
    isAdmin,
    hasRole: (role: UserRole) => {
      if (role === 'admin') {
        return isAdmin
      }
      return isUserRole(role)
    },

    // Actual role checking (ignores admin view toggle - for authentication/security)
    isActualAdmin,
    isActualUser,
    hasActualRole: (role: UserRole) => isUserRole(role),

    // Role requirements (throws errors if not met - uses actual roles for security)
    requireUser: () => requireRole('user'),
    requireAdmin: () => requireRole('admin'),
    requireRole: (role: UserRole) => requireRole(role),

    // Convenience properties
    canAccessUserFeatures: isActualUser,
    canAccessAdminFeatures,

    // Role information
    currentRole: user?.role || null,
    isAdminUser: user?.role === 'admin' || false,
    customClaims: user?.customClaims || {},

    // Admin view state
    isAdminViewEnabled,
  }
}
